
import { motion } from 'framer-motion';

const GlobalNetwork = () => {
  const regions = [
    { name: 'Europe', countries: 15, icon: 'ri-building-line', position: 'top-[28%] left-[48%]' },
    { name: 'North America', countries: 8, icon: 'ri-building-4-line', position: 'top-[32%] left-[22%]' },
    { name: 'South America', countries: 6, icon: 'ri-community-line', position: 'top-[58%] left-[28%]' },
    { name: 'Africa', countries: 10, icon: 'ri-home-4-line', position: 'top-[48%] left-[50%]' },
    { name: 'Asia', countries: 12, icon: 'ri-store-2-line', position: 'top-[35%] left-[72%]' },
    { name: 'Oceania', countries: 4, icon: 'ri-hotel-line', position: 'top-[62%] left-[82%]' },
  ];

  const routes = [
    { from: 'Europe', to: 'North America', delay: 0 },
    { from: 'Europe', to: 'Asia', delay: 0.5 },
    { from: 'North America', to: 'Asia', delay: 1 },
    { from: 'Europe', to: 'Africa', delay: 1.5 },
    { from: 'Asia', to: 'Oceania', delay: 2 },
    { from: 'South America', to: 'Africa', delay: 2.5 },
  ];

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block px-4 py-2 bg-primary-blue/10 rounded-lg text-sm font-semibold text-primary-blue uppercase tracking-wider mb-4 whitespace-nowrap">
            Global Presence
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl text-gray-900 font-bold mb-4">
            Connected Across the World
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our extensive network spans 50+ countries, ensuring your shipments reach any destination with precision and reliability.
          </p>
        </motion.div>

        <div className="relative">
          {/* World Map Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative w-full aspect-[2/1] min-h-[500px] rounded-2xl overflow-hidden bg-primary-navy/5"
          >
            {/* World Map Image */}
            <img
              src="https://public.readdy.ai/ai/img_res/7ee3891aba2be589170e9031a5f029e6.jpg"
              alt="TrackVerse Global Network Map"
              className="absolute inset-0 w-full h-full object-cover opacity-90"
            />

            {/* Overlay for better contrast */}
            <div className="absolute inset-0 bg-white/30"></div>

            {/* Animated Connection Lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 50" preserveAspectRatio="none">
              {/* Europe to North America */}
              <motion.path
                d="M 48 14 Q 35 8 22 16"
                fill="none"
                stroke="#0066CC"
                strokeWidth="0.15"
                strokeDasharray="1 0.5"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.6 }}
                viewport={{ once: true }}
                transition={{ duration: 2, delay: 0 }}
              />
              {/* Europe to Asia */}
              <motion.path
                d="M 48 14 Q 60 12 72 17"
                fill="none"
                stroke="#00CC88"
                strokeWidth="0.15"
                strokeDasharray="1 0.5"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.6 }}
                viewport={{ once: true }}
                transition={{ duration: 2, delay: 0.5 }}
              />
              {/* North America to Asia */}
              <motion.path
                d="M 22 16 Q 47 5 72 17"
                fill="none"
                stroke="#0066CC"
                strokeWidth="0.15"
                strokeDasharray="1 0.5"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.6 }}
                viewport={{ once: true }}
                transition={{ duration: 2, delay: 1 }}
              />
              {/* Europe to Africa */}
              <motion.path
                d="M 48 14 Q 49 20 50 24"
                fill="none"
                stroke="#00CC88"
                strokeWidth="0.15"
                strokeDasharray="1 0.5"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.6 }}
                viewport={{ once: true }}
                transition={{ duration: 2, delay: 1.5 }}
              />
              {/* Asia to Oceania */}
              <motion.path
                d="M 72 17 Q 77 25 82 31"
                fill="none"
                stroke="#0066CC"
                strokeWidth="0.15"
                strokeDasharray="1 0.5"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.6 }}
                viewport={{ once: true }}
                transition={{ duration: 2, delay: 2 }}
              />
              {/* South America to Africa */}
              <motion.path
                d="M 28 29 Q 39 28 50 24"
                fill="none"
                stroke="#00CC88"
                strokeWidth="0.15"
                strokeDasharray="1 0.5"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.6 }}
                viewport={{ once: true }}
                transition={{ duration: 2, delay: 2.5 }}
              />
            </svg>

            {/* Region Markers */}
            {regions.map((region, index) => (
              <motion.div
                key={region.name}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className={`absolute ${region.position} transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer`}
              >
                {/* Pulse Animation */}
                <div className="absolute inset-0 w-4 h-4 bg-primary-blue/30 rounded-full animate-ping"></div>
                
                {/* Marker Dot */}
                <div className="relative w-4 h-4 bg-primary-blue rounded-full border-2 border-white shadow-lg group-hover:scale-125 transition-transform"></div>
                
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="bg-white rounded-lg shadow-xl px-4 py-3 whitespace-nowrap">
                    <div className="font-semibold text-gray-900 text-sm">{region.name}</div>
                    <div className="text-xs text-primary-blue font-medium">{region.countries} Countries</div>
                  </div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="w-2 h-2 bg-white transform rotate-45"></div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Animated Packages */}
            <motion.div
              animate={{
                x: ['0%', '100%'],
                y: ['0%', '-20%', '0%'],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'linear',
              }}
              className="absolute top-[30%] left-[20%] w-3 h-3 bg-primary-green rounded-sm shadow-lg"
            >
              <i className="ri-box-3-fill text-white text-[8px] flex items-center justify-center w-full h-full"></i>
            </motion.div>

            <motion.div
              animate={{
                x: ['100%', '0%'],
                y: ['0%', '30%', '0%'],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: 'linear',
                delay: 2,
              }}
              className="absolute top-[25%] left-[60%] w-3 h-3 bg-primary-blue rounded-sm shadow-lg"
            >
              <i className="ri-box-3-fill text-white text-[8px] flex items-center justify-center w-full h-full"></i>
            </motion.div>
          </motion.div>

          {/* Region Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
            {regions.map((region, index) => (
              <motion.div
                key={region.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow border border-gray-100 text-center cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-primary-blue/10 rounded-lg mx-auto mb-3 group-hover:bg-primary-blue transition-colors">
                  <i className={`${region.icon} text-primary-blue text-xl group-hover:text-white transition-colors`}></i>
                </div>
                <div className="font-semibold text-gray-900 text-sm mb-1">{region.name}</div>
                <div className="text-xs text-gray-500">{region.countries} Countries</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Key Routes Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-primary-blue/5 rounded-xl p-6 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-primary-blue rounded-xl mx-auto mb-4">
              <i className="ri-ship-line text-white text-2xl"></i>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">150+</div>
            <div className="text-gray-600">Sea Routes</div>
          </div>
          <div className="bg-primary-green/5 rounded-xl p-6 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-primary-green rounded-xl mx-auto mb-4">
              <i className="ri-plane-line text-white text-2xl"></i>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">200+</div>
            <div className="text-gray-600">Air Connections</div>
          </div>
          <div className="bg-primary-blue/5 rounded-xl p-6 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-primary-blue rounded-xl mx-auto mb-4">
              <i className="ri-truck-line text-white text-2xl"></i>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">500+</div>
            <div className="text-gray-600">Land Routes</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalNetwork;
